import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import type { DealProductWithLocation } from "@/models/deal/deal.types";
import type { ProductType } from "@/models/product";

const getDealId = () =>
    new URLSearchParams(window.location.search).get("dealId") ?? "default";

export type ControlType = "REMOTE" | "APP";

export type WindowFabricColor = {
    windowId: string;
    fabricType: "TRANSLUCENT" | "SUN_BLOCKING";
    colorId: string;
    colorHex: string | null;
    colorName: string | null;
    pricePerCm2: number | null;
    frameColorId: string | null;
    frameColorName: string | null;
    frameColorHex: string | null;
    // Sun-protection products only. Defaults to REMOTE on first selection;
    // window-decoration windows keep this null and the mapper falls back to
    // REMOTE when building the GraphQL payload.
    controlType: ControlType | null;
};

export type ConfigurationSnapshot = {
    selectedProductIds: string[];
    windowSelections: Record<string, WindowFabricColor>;
};

const emptySnapshot: ConfigurationSnapshot = {
    selectedProductIds: [],
    windowSelections: {},
};

const comboKey = (category: ProductType, typeId: string) =>
    `${category}:${typeId}`;

export type ConfiguredCombo = {
    category: ProductType;
    typeId: string;
    selectedProductIds: string[];
    windowSelections: Record<string, WindowFabricColor>;
};

export function getAllConfiguredCombos(
    state: SelectionState,
): ConfiguredCombo[] {
    const combos: ConfiguredCombo[] = [];
    const activeKey =
        state.selectedCategory && state.selectedTypeId
            ? comboKey(state.selectedCategory, state.selectedTypeId)
            : null;

    if (
        state.selectedCategory &&
        state.selectedTypeId &&
        state.selectedProductIds.length > 0
    ) {
        combos.push({
            category: state.selectedCategory,
            typeId: state.selectedTypeId,
            selectedProductIds: state.selectedProductIds,
            windowSelections: state.windowSelections,
        });
    }

    for (const [key, snapshot] of Object.entries(state.cachedByCombo)) {
        if (key === activeKey) continue;
        if (snapshot.selectedProductIds.length === 0) continue;
        const sep = key.indexOf(":");
        if (sep === -1) continue;
        const category = key.slice(0, sep) as ProductType;
        const typeId = key.slice(sep + 1);
        if (!typeId) continue;

        combos.push({
            category,
            typeId,
            selectedProductIds: snapshot.selectedProductIds,
            windowSelections: snapshot.windowSelections,
        });
    }

    return combos;
}

// Returns the cached combo (different typeId, same category as the current
// active selection) that already claims this windowId, if any. Used by
// WindowsStep to surface a "Replace?" dialog before letting the toggle
// create a backend-invalid duplicate (a window can hold at most one
// sun-protection product and one window-decoration product).
export function findConflictingSameCategoryCombo(
    state: SelectionState,
    windowId: string,
): { category: ProductType; typeId: string } | null {
    if (!state.selectedCategory || !state.selectedTypeId) return null;
    const activeKey = `${state.selectedCategory}:${state.selectedTypeId}`;
    for (const [key, snapshot] of Object.entries(state.cachedByCombo)) {
        if (key === activeKey) continue;
        if (!snapshot.selectedProductIds.includes(windowId)) continue;
        const sep = key.indexOf(":");
        const category = key.slice(0, sep) as ProductType;
        if (category !== state.selectedCategory) continue;
        const typeId = key.slice(sep + 1);
        return { category, typeId };
    }
    return null;
}

export type SelectionState = {
    selectedCategory: ProductType | null;
    selectedTypeId: string | null;
    dealProducts: DealProductWithLocation[];
    selectedProductIds: string[];
    windowSelections: Record<string, WindowFabricColor>;
    cachedByCombo: Record<string, ConfigurationSnapshot>;
    lastTypeIdByCategory: Partial<Record<ProductType, string>>;
};

type SelectionActions = {
    selectCategory: (category: ProductType) => void;
    selectType: (id: string) => void;
    setDealProducts: (products: DealProductWithLocation[]) => void;
    toggleProductId: (id: string) => void;
    setWindowSelection: (sel: WindowFabricColor) => void;
    removeProductFromCombo: (
        category: ProductType,
        typeId: string,
        windowId: string,
    ) => void;
    replaceProductOnWindow: (
        windowId: string,
        fromCategory: ProductType,
        fromTypeId: string,
    ) => void;
    reset: () => void;
};

export const useSelectionStore = create<SelectionState & SelectionActions>()(
    persist(
        (set) => ({
            selectedCategory: null,
            selectedTypeId: null,
            dealProducts: [],
            selectedProductIds: [],
            windowSelections: {},
            cachedByCombo: {},
            lastTypeIdByCategory: {},
            selectCategory: (category) =>
                set((state) => {
                    if (state.selectedCategory === category) return state;
                    const cache = { ...state.cachedByCombo };
                    const lastType = { ...state.lastTypeIdByCategory };
                    if (state.selectedCategory && state.selectedTypeId) {
                        cache[
                            comboKey(
                                state.selectedCategory,
                                state.selectedTypeId,
                            )
                        ] = {
                            selectedProductIds: state.selectedProductIds,
                            windowSelections: state.windowSelections,
                        };
                        lastType[state.selectedCategory] = state.selectedTypeId;
                    }
                    const nextTypeId = lastType[category] ?? null;
                    const nextSnapshot =
                        nextTypeId && cache[comboKey(category, nextTypeId)]
                            ? cache[comboKey(category, nextTypeId)]
                            : emptySnapshot;
                    return {
                        selectedCategory: category,
                        selectedTypeId: nextTypeId,
                        selectedProductIds: nextSnapshot.selectedProductIds,
                        windowSelections: nextSnapshot.windowSelections,
                        cachedByCombo: cache,
                        lastTypeIdByCategory: lastType,
                    };
                }),
            selectType: (id) =>
                set((state) => {
                    if (state.selectedTypeId === id) return state;
                    if (!state.selectedCategory) {
                        return { selectedTypeId: id };
                    }
                    const cache = { ...state.cachedByCombo };
                    const lastType = { ...state.lastTypeIdByCategory };
                    if (state.selectedTypeId) {
                        cache[
                            comboKey(
                                state.selectedCategory,
                                state.selectedTypeId,
                            )
                        ] = {
                            selectedProductIds: state.selectedProductIds,
                            windowSelections: state.windowSelections,
                        };
                    }
                    lastType[state.selectedCategory] = id;
                    const nextSnapshot =
                        cache[comboKey(state.selectedCategory, id)] ??
                        emptySnapshot;
                    return {
                        selectedTypeId: id,
                        selectedProductIds: nextSnapshot.selectedProductIds,
                        windowSelections: nextSnapshot.windowSelections,
                        cachedByCombo: cache,
                        lastTypeIdByCategory: lastType,
                    };
                }),
            setDealProducts: (products) => set({ dealProducts: products }),
            toggleProductId: (id) =>
                set((state) => {
                    // Deselecting drops the window's stored color too, so a
                    // removed frame never keeps an orphan configuration that
                    // would still surface in pricing/confirm.
                    if (state.selectedProductIds.includes(id)) {
                        const nextSelections = { ...state.windowSelections };
                        delete nextSelections[id];
                        return {
                            selectedProductIds: state.selectedProductIds.filter(
                                (x) => x !== id,
                            ),
                            windowSelections: nextSelections,
                        };
                    }
                    return {
                        selectedProductIds: [...state.selectedProductIds, id],
                    };
                }),
            setWindowSelection: (sel) =>
                set((state) => ({
                    windowSelections: {
                        ...state.windowSelections,
                        [sel.windowId]: sel,
                    },
                })),
            replaceProductOnWindow: (windowId, fromCategory, fromTypeId) =>
                set((state) => {
                    const key = comboKey(fromCategory, fromTypeId);
                    const cachedSnapshot = state.cachedByCombo[key];
                    if (!cachedSnapshot) return state;
                    const nextCachedSelections = {
                        ...cachedSnapshot.windowSelections,
                    };
                    delete nextCachedSelections[windowId];
                    return {
                        selectedProductIds: state.selectedProductIds.includes(
                            windowId,
                        )
                            ? state.selectedProductIds
                            : [...state.selectedProductIds, windowId],
                        cachedByCombo: {
                            ...state.cachedByCombo,
                            [key]: {
                                selectedProductIds:
                                    cachedSnapshot.selectedProductIds.filter(
                                        (id) => id !== windowId,
                                    ),
                                windowSelections: nextCachedSelections,
                            },
                        },
                    };
                }),
            removeProductFromCombo: (category, typeId, windowId) =>
                set((state) => {
                    const isActive =
                        state.selectedCategory === category &&
                        state.selectedTypeId === typeId;
                    if (isActive) {
                        const nextSelections = { ...state.windowSelections };
                        delete nextSelections[windowId];
                        return {
                            selectedProductIds: state.selectedProductIds.filter(
                                (id) => id !== windowId,
                            ),
                            windowSelections: nextSelections,
                        };
                    }
                    const key = comboKey(category, typeId);
                    const snapshot = state.cachedByCombo[key];
                    if (!snapshot) return state;
                    const nextSelections = { ...snapshot.windowSelections };
                    delete nextSelections[windowId];
                    return {
                        cachedByCombo: {
                            ...state.cachedByCombo,
                            [key]: {
                                selectedProductIds:
                                    snapshot.selectedProductIds.filter(
                                        (id) => id !== windowId,
                                    ),
                                windowSelections: nextSelections,
                            },
                        },
                    };
                }),
            reset: () =>
                set({
                    selectedCategory: null,
                    selectedTypeId: null,
                    selectedProductIds: [],
                    windowSelections: {},
                    cachedByCombo: {},
                    lastTypeIdByCategory: {},
                }),
        }),
        {
            name: "sun-protection:selection",
            storage: createJSONStorage(() => ({
                getItem: (name) =>
                    localStorage.getItem(`${name}:${getDealId()}`),
                setItem: (name, value) =>
                    localStorage.setItem(`${name}:${getDealId()}`, value),
                removeItem: (name) =>
                    localStorage.removeItem(`${name}:${getDealId()}`),
            })),
            partialize: (state) => ({
                selectedCategory: state.selectedCategory,
                selectedTypeId: state.selectedTypeId,
                selectedProductIds: state.selectedProductIds,
                windowSelections: state.windowSelections,
                cachedByCombo: state.cachedByCombo,
                lastTypeIdByCategory: state.lastTypeIdByCategory,
            }),
        },
    ),
);
